"use client";

// The compound root: `<RelayRecovery>` + `.Trigger` + `.Content` — the
// standalone "Recover stuck funds" modal, i.e. the deposit wizard's gear panel
// as a modal of its own (the gear inside `<RelayDeposit>` is untouched and
// still opt-in via `showRecovery`).
//
// Structurally `RelayHistory.tsx`: every prop goes into a context so `.Content`,
// supplied by the host as a child, can read them; the shell knobs go into
// `ResponsiveShell`; and there is no wizard state to lift above the shell
// (unlike `RelayDeposit`, which needs `RelayWizardStateHost` for that).
//
// It does own two things beyond the history modal:
//
//   * the modal's OPEN flag, resolved here and driving `ResponsiveShell` fully
//     controlled (verbatim from `RelayDepositRoot`, and for a related reason:
//     something above the shell has to know when the modal closes);
//   * the shared recovery snapshot (`useRecoverableCount`). It has to be fetched
//     HERE because the trigger needs its count while closed. The modal reuses the
//     same per-target data instead of repeating the fan-out on every open.
import * as React from "react";

import { useRecoverableCount } from "../hooks/useRecoverableCount";
import { resolveRelayApiBaseUrl } from "../lib/api-base";
import { createRelayClient } from "../lib/client";
import { RelayRecoveryContext } from "../providers/relay-recovery-provider";
import type { RelayRecoveryProps } from "../types";
import { isAddress } from "../lib/units";
import { RelayRecoveryContent } from "./RelayRecoveryContent";
import { RelayRecoveryTrigger } from "./RelayRecoveryTrigger";
import { ResponsiveShell } from "./shell/ResponsiveShell";
import "../styles/component.css";

function RelayRecoveryRoot(props: RelayRecoveryProps) {
  const {
    theme = "dark",
    mobileBreakpoint,
    drawerDirection,
    dialogWidth,
    drawerMaxWidth,
    drawerMinWidth,
    style,
    open: openProp,
    onOpenChange,
    children,
  } = props;

  // Same controlled/uncontrolled contract `ResponsiveShell` implements, resolved
  // one level higher (verbatim from `RelayDepositRoot`): an `open` prop wins
  // outright, `onOpenChange` is notified either way, and the shell below is
  // always driven fully controlled.
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  const handleOpenChange = React.useCallback(
    (value: boolean) => {
      if (!isControlled) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange],
  );

  // Same resolution order the content uses (`client` > `apiBaseUrl` >
  // `environment`), so the badge and the panel always talk to one backend.
  const client = React.useMemo(() => {
    if (props.client) return props.client;
    const baseUrl = resolveRelayApiBaseUrl({
      apiBaseUrl: props.apiBaseUrl,
      environment: props.environment,
    });
    return createRelayClient({ baseUrl });
  }, [props.client, props.apiBaseUrl, props.environment]);

  const connectedWallet =
    props.walletAddress && isAddress(props.walletAddress)
      ? props.walletAddress
      : undefined;
  const recoveryRecipient =
    connectedWallet &&
    (!props.recipient ||
      (isAddress(props.recipient) &&
        props.recipient.toLowerCase() === connectedWallet.toLowerCase()))
      ? connectedWallet
      : undefined;

  const recovery = useRecoverableCount({
    client,
    // Recovery is signing-first. Never prefetch another address merely because
    // the public API accepts `?user=`; the UI is pinned to the connected wallet.
    recipient: recoveryRecipient,
  });

  // A later open may need fresher balances, but rapid close/reopen cycles reuse
  // the recent snapshot. The hook also coalesces an open that happens while the
  // initial closed-trigger request is still in flight.
  React.useEffect(() => {
    if (open) recovery.refreshIfStale();
  }, [open, recovery.refreshIfStale]);

  // Report each failed snapshot once from the persistent root. The modal body
  // unmounts on close, so reporting there repeated the same cached error on
  // every reopen (and whenever an inline callback changed identity).
  const reportedError = React.useRef<Error | undefined>(undefined);
  React.useEffect(() => {
    if (!open || !recovery.error || reportedError.current === recovery.error) return;
    reportedError.current = recovery.error;
    props.onError?.(recovery.error);
  }, [open, recovery.error, props.onError]);

  // `props` is a fresh object each render, so consumers re-render exactly when
  // this root does -- same rationale as RelayHistoryRoot (memoizing it would
  // only pretend to be stable; every field is a prop). Note that the recovery
  // body does NOT key any effect on this object's identity: it re-bundles the
  // injected wallet functions through its own memo first (see
  // RelayRecoveryContent's `wallet`).
  const contextValue = React.useMemo(
    () => ({ props, recovery }),
    [props, recovery],
  );

  return (
    <RelayRecoveryContext.Provider value={contextValue}>
      <ResponsiveShell
        theme={theme}
        mobileBreakpoint={mobileBreakpoint}
        drawerDirection={drawerDirection}
        dialogWidth={dialogWidth}
        drawerMaxWidth={drawerMaxWidth}
        drawerMinWidth={drawerMinWidth}
        style={style}
        open={open}
        onOpenChange={handleOpenChange}
      >
        {children}
      </ResponsiveShell>
    </RelayRecoveryContext.Provider>
  );
}

export const RelayRecovery = Object.assign(RelayRecoveryRoot, {
  Trigger: RelayRecoveryTrigger,
  Content: RelayRecoveryContent,
});
